import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Trash2, 
  Upload, 
  Image as ImageIcon,
  Eye,
  Edit3
} from 'lucide-react';
import { heroSectionAPI, getImageUrl } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  HeroSectionContainer,
  SectionHeader,
  Title,
  Subtitle,
  Form,
  FormGroup,
  Label,
  Input,
  TextArea,
  ServicesContainer,
  ServiceCard,
  ServiceHeader,
  ServiceTitle,
  ServiceActions,
  ActionButton,
  ImagePreview,
  ImagePlaceholder,
  ImageUpload,
  ImageInput,
  UploadButton,
  SubmitButton,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
  PreviewSection,
  PreviewTitle,
  PreviewCard,
  PreviewImage,
  PreviewContent,
  PreviewServiceTitle,
  PreviewServiceDescription,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText
} from './HeroSection.styles';

const HeroSection = () => {
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    heading: '',
    button_text: '',
    services: []
  });
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    loadHeroSection();
  }, []);

  const loadHeroSection = async () => {
    try {
      setLoading(true);
      const response = await heroSectionAPI.get();
      if (response.status === 200) {
        const data = response.data;
        setExistingData(data);
        setFormData({
          heading: data.heading || '',
          button_text: data.button_text || '',
          services: data.services || []
        });
        
        // Load existing images
        const imageMap = {};
        data.services?.forEach((service, index) => {
          if (service.image_path) {
            imageMap[index] = service.image_path;
          }
        });
        setImages(imageMap);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        const errorMessage = error.response?.data?.message || 'Failed to load hero section data';
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map((service, i) => 
        i === index ? { ...service, [field]: value } : service
      )
    }));
  };

  const addService = () => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, { title: '', description: '' }]
    }));
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
    
    // Remove associated image
    setImages(prev => {
      const newImages = { ...prev };
      delete newImages[index];
      return newImages;
    });
  };

  const handleImageUpload = (index, file) => {
    if (file) {
      setImages(prev => ({
        ...prev,
        [index]: file
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.heading.trim()) {
      setError('Heading is required');
      return;
    }

    if (!formData.button_text.trim()) {
      setError('Button text is required');
      return;
    }

    if (formData.services.length === 0) {
      setError('At least one service is required');
      return;
    }

    for (let i = 0; i < formData.services.length; i++) {
      const service = formData.services[i];
      if (!service.title.trim() || !service.description.trim()) {
        setError(`Service ${i + 1}: Title and description are required`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = new FormData();
      submitData.append('heading', formData.heading);
      submitData.append('button_text', formData.button_text);
      submitData.append('services', JSON.stringify(formData.services));

      // Add image files
      Object.keys(images).forEach(index => {
        if (images[index] instanceof File) {
          submitData.append(`service_${index}_image`, images[index]);
        }
      });

      const response = existingData 
        ? await heroSectionAPI.update(submitData)
        : await heroSectionAPI.create(submitData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(existingData ? 'Hero section updated successfully!' : 'Hero section created successfully!');
        setExistingData(response.data);
        loadHeroSection(); // Reload to get updated data
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save hero section';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !existingData) {
    return (
      <HeroSectionContainer>
        <LoadingSpinner />
      </HeroSectionContainer>
    );
  }

  return (
    <HeroSectionContainer>
      <SectionHeader>
        <Title>Hero Section Management</Title>
        <Subtitle>Manage your website's hero section with heading, button text, and services</Subtitle>
      </SectionHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="heading">Hero Heading *</Label>
          <Input
            id="heading"
            type="text"
            value={formData.heading}
            onChange={(e) => handleInputChange('heading', e.target.value)}
            placeholder="Enter your hero heading"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="button_text">Button Text *</Label>
          <Input
            id="button_text"
            type="text"
            value={formData.button_text}
            onChange={(e) => handleInputChange('button_text', e.target.value)}
            placeholder="e.g., Get Started, Learn More, Contact Us"
            required
          />
        </FormGroup>

        <ServicesContainer>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <Label>Services *</Label>
            <ActionButton type="button" onClick={addService}>
              <Plus size={16} />
              Add Service
            </ActionButton>
          </div>

          {formData.services.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <ImageIcon size={48} />
              </EmptyStateIcon>
              <EmptyStateText>No services added yet. Click "Add Service" to get started.</EmptyStateText>
            </EmptyState>
          ) : (
            formData.services.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceHeader>
                  <ServiceTitle>Service {index + 1}</ServiceTitle>
                  <ServiceActions>
                    <ActionButton 
                      type="button" 
                      onClick={() => removeService(index)}
                      variant="danger"
                    >
                      <Trash2 size={16} />
                    </ActionButton>
                  </ServiceActions>
                </ServiceHeader>

                <FormGroup>
                  <Label>Service Title *</Label>
                  <Input
                    type="text"
                    value={service.title}
                    onChange={(e) => handleServiceChange(index, 'title', e.target.value)}
                    placeholder="Enter service title"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Service Description *</Label>
                  <TextArea
                    value={service.description}
                    onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                    placeholder="Enter service description"
                    rows={3}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Service Image</Label>
                  <ImageUpload>
                    {images[index] ? (
                      <ImagePreview>
                        {images[index] instanceof File ? (
                          <img 
                            src={URL.createObjectURL(images[index])} 
                            alt={`Service ${index + 1} preview`}
                          />
                        ) : (
                          <img 
                            src={getImageUrl(images[index])} 
                            alt={`Service ${index + 1}`}
                          />
                        )}
                        <ImageInput
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(index, e.target.files[0])}
                        />
                        <UploadButton>
                          <Edit3 size={16} />
                          Change Image
                        </UploadButton>
                      </ImagePreview>
                    ) : (
                      <ImagePlaceholder>
                        <ImageIcon size={32} />
                        <span>No image selected</span>
                        <ImageInput
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(index, e.target.files[0])}
                        />
                        <UploadButton>
                          <Upload size={16} />
                          Upload Image
                        </UploadButton>
                      </ImagePlaceholder>
                    )}
                  </ImageUpload>
                </FormGroup>
              </ServiceCard>
            ))
          )}
          
          {formData.services.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
              <ActionButton type="button" onClick={addService}>
                <Plus size={16} />
                Add Another Service
              </ActionButton>
            </div>
          )}
        </ServicesContainer>

        <SubmitButton type="submit" disabled={loading}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <Save size={20} />
              {existingData ? 'Update Hero Section' : 'Create Hero Section'}
            </>
          )}
        </SubmitButton>
      </Form>

      {existingData && (
        <PreviewSection>
          <PreviewTitle>Preview</PreviewTitle>
          <PreviewCard>
            <h2 style={{ color: '#C5337B', marginBottom: '1rem' }}>
              {existingData.heading}
            </h2>
            <div style={{ marginBottom: '1.5rem' }}>
              <button style={{
                background: '#C5337B',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}>
                {existingData.button_text}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
              {existingData.services?.map((service, index) => (
                <div key={index} style={{ 
                  background: 'rgba(197, 51, 123, 0.1)', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  border: '1px solid rgba(197, 51, 123, 0.2)'
                }}>
                  {service.image_path && (
                    <PreviewImage src={getImageUrl(service.image_path)} alt={service.title} />
                  )}
                  <PreviewContent>
                    <PreviewServiceTitle>{service.title}</PreviewServiceTitle>
                    <PreviewServiceDescription>{service.description}</PreviewServiceDescription>
                  </PreviewContent>
                </div>
              ))}
            </div>
          </PreviewCard>
        </PreviewSection>
      )}

    </HeroSectionContainer>
  );
};

export default HeroSection;
